import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight, CheckCircle, Zap, Shield, RotateCcw, Globe2, Wrench, Settings } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Reveal } from '../hooks/useReveal';

const TRANSFORMER_1 = 'https://customer-assets.emergentagent.com/job_voltage-solutions-2/artifacts/3wifgtrh_WhatsApp%20Image%202024-11-17%20at%2021.04.50.jpeg';
const TRANSFORMER_2 = 'https://customer-assets.emergentagent.com/job_voltage-solutions-2/artifacts/n2sltbd7_WhatsApp%20Image%202024-11-17%20at%2021.04.45%20%281%29.jpeg';
const CUSTOM_HERO = 'https://customer-assets.emergentagent.com/job_voltage-solutions-2/artifacts/cpi7grdp_WhatsApp%20Image%202026-05-30%20at%2014.16.26.jpeg';
const CUSTOM_SIDE = 'https://customer-assets.emergentagent.com/job_voltage-solutions-2/artifacts/2jrw5sxa_WhatsApp%20Image%202026-05-30%20at%2014.00.09.jpeg';
const CUSTOM_ISO = 'https://customer-assets.emergentagent.com/job_voltage-solutions-2/artifacts/9raw9ghf_1746633955088.jpg';
const CUSTOM_ACTIVE = 'https://customer-assets.emergentagent.com/job_voltage-solutions-2/artifacts/i7culh6s_1746633956242.jpg';
const CUSTOM_ASSEMBLY = 'https://customer-assets.emergentagent.com/job_voltage-solutions-2/artifacts/i2cqt4qd_WhatsApp%20Image%202026-05-30%20at%2014.21.36.jpeg';
const CUSTOM_CORE = 'https://customer-assets.emergentagent.com/job_voltage-solutions-2/artifacts/ocsy1269_WhatsApp%20Image%202026-05-30%20at%2013.43.51.jpeg';

const customContent = {
  tr: {
    heroTitle: 'Projenize Ozel Guc Cozumleri',
    heroP1: 'Astavolt olarak yalnizca standart dagitim trafolari degil, musterilerimizin ihtiyaclarina ozel olarak tasarlanan ve uretilen ozel tip trafolar konusunda da kapsamli cozumler sunuyoruz.',
    heroP2: 'Muhendislik ekibimiz tarafindan projeye ozel olarak tasarlanan trafolarimiz; enerji uretim tesislerinden endustriyel fabrikalara, yenilenebilir enerji santrallerinden altyapi projelerine kadar bircok kritik uygulamada guvenle kullanilmaktadir.',
    heroP3: 'Farkli gerilim seviyeleri, guc degerleri, baglanti gruplari ve mekanik tasarim secenekleriyle her projeye uygun cozumler gelistiriyor; uluslararasi standartlara uygun uretim gerceklestiriyoruz.',
    typesTitle: 'Uretimini Gerceklestirdigimiz Ozel Tip Trafolar',
    types: [
      { title: 'Tek Fazli Trafolar', desc: 'Demiryolu sistemleri, ozel enerji uygulamalari ve endustriyel projeler icin farkli guc ve gerilim seviyelerinde tek fazli trafo uretimi gerceklestiriyoruz.' },
      { title: 'Izolasyon Trafolari', desc: 'Hassas yuklerin korunmasi ve galvanik izolasyon saglanmasi amaciyla tasarlanan izolasyon trafolarimiz; hastaneler, veri merkezleri ve kritik tesislerde guvenli enerji surekliligi saglar.' },
      { title: 'Oto Trafolar', desc: 'Yuksek verimlilik ve ekonomik cozum sunan oto trafolarimiz, gerilim donusumu ve motor yol verme uygulamalarinda tercih edilmektedir.' },
      { title: 'Topraklama Trafolari', desc: 'Notr noktasi bulunmayan sebekelerde yapay notr olusturmak ve sistem guvenligini artirmak amaciyla kullanilan topraklama trafolarini proje ihtiyaclarina gore tasarliyor ve uretiyoruz.' },
      { title: 'Yandan Cikisli Trafolar', desc: 'Saha sartlarina uygun ozel mekanik tasarimlarla uretilen yandan cikisli trafolarimiz, dar alanlarda montaj kolayligi ve operasyonel avantaj saglar.' },
      { title: 'Ihtiyaciniza Uygun Ozel Tasarim', desc: 'Her proje farklidir. Bu nedenle Astavolt olarak standart cozumlerle sinirli kalmiyor, musterilerimizin teknik sartnamelerine uygun ozel tasarim ve ozel uretim trafo cozumleri gelistiriyoruz.' },
    ],
    features: ['Projeye ozel tasarim', 'IEC ve ilgili uluslararasi standartlara uygun uretim', 'Fabrika testleri ve kalite kontrol surecleri', 'Yerli uretim gucu', 'Muhendislik destegi', 'Hizli termin ve teknik servis destegi'],
    featureFooter: 'Enerji sistemleriniz icin guvenilir, verimli ve uzun omurlu trafo cozumleri konusunda uzman ekibimizle yaninizdayiz.',
    slogan1: 'Standartlarin Otesinde, Projenize Ozel Trafo Cozumleri',
    slogan2: 'Ihtiyaciniz Ne Olursa Olsun, Astavolt Uretiyor.',
  },
  en: {
    heroTitle: 'Custom Power Solutions for Your Project',
    heroP1: 'At Astavolt, we offer comprehensive solutions not only for standard distribution transformers, but also for special type transformers designed and manufactured specifically for our customers\' needs.',
    heroP2: 'Our transformers, designed specifically for each project by our engineering team, are safely used in many critical applications from energy production facilities to industrial factories, from renewable energy plants to infrastructure projects.',
    heroP3: 'We develop solutions suitable for every project with different voltage levels, power ratings, connection groups and mechanical design options; producing in compliance with international standards.',
    typesTitle: 'Special Type Transformers We Manufacture',
    types: [
      { title: 'Single Phase Transformers', desc: 'We manufacture single-phase transformers at different power and voltage levels for railway systems, special energy applications and industrial projects.' },
      { title: 'Isolation Transformers', desc: 'Our isolation transformers, designed to protect sensitive loads and provide galvanic isolation, ensure safe energy continuity in hospitals, data centers and critical facilities.' },
      { title: 'Auto Transformers', desc: 'Our auto transformers offering high efficiency and economical solutions are preferred in voltage conversion and motor starting applications.' },
      { title: 'Grounding Transformers', desc: 'We design and manufacture grounding transformers used to create artificial neutral in networks without a neutral point and to increase system safety.' },
      { title: 'Side Exit Transformers', desc: 'Our side exit transformers manufactured with special mechanical designs suitable for field conditions provide ease of installation and operational advantages in narrow spaces.' },
      { title: 'Custom Design for Your Needs', desc: 'Every project is different. Therefore, at Astavolt we develop custom design and custom production transformer solutions that comply with our customers\' technical specifications.' },
    ],
    features: ['Project-specific design', 'Production compliant with IEC and international standards', 'Factory tests and quality control processes', 'Domestic production power', 'Engineering support', 'Fast delivery and technical service support'],
    featureFooter: 'Our expert team is by your side for reliable, efficient and long-lasting transformer solutions for your energy systems.',
    slogan1: 'Beyond Standards, Custom Transformer Solutions for Your Project',
    slogan2: 'Whatever Your Need, Astavolt Produces.',
  },
  fr: {
    heroTitle: 'Solutions de Puissance Sur Mesure',
    heroP1: 'Chez Astavolt, nous offrons des solutions completes pour les transformateurs de type special concus et fabriques specifiquement pour les besoins de nos clients.',
    heroP2: 'Nos transformateurs, concus pour chaque projet par notre equipe d\'ingenierie, sont utilises dans de nombreuses applications critiques.',
    heroP3: 'Nous developpons des solutions adaptees a chaque projet avec differents niveaux de tension, puissances et options de conception.',
    typesTitle: 'Transformateurs de Type Special que Nous Fabriquons',
    types: [
      { title: 'Transformateurs Monophases', desc: 'Fabrication de transformateurs monophases pour systemes ferroviaires et projets industriels.' },
      { title: 'Transformateurs d\'Isolation', desc: 'Nos transformateurs d\'isolation assurent la continuite energetique dans les hopitaux et centres de donnees.' },
      { title: 'Autotransformateurs', desc: 'Nos autotransformateurs offrent haute efficacite pour la conversion de tension.' },
      { title: 'Transformateurs de Mise a la Terre', desc: 'Transformateurs de mise a la terre pour creer un neutre artificiel dans les reseaux.' },
      { title: 'Transformateurs a Sortie Laterale', desc: 'Nos transformateurs a sortie laterale offrent facilite d\'installation dans les espaces reduits.' },
      { title: 'Conception Sur Mesure', desc: 'Chaque projet est different. Nous developpons des solutions de transformateurs personnalisees.' },
    ],
    features: ['Conception specifique au projet', 'Production conforme IEC', 'Tests en usine', 'Puissance de production locale', 'Support d\'ingenierie', 'Livraison rapide et service technique'],
    featureFooter: 'Notre equipe d\'experts est a vos cotes pour des solutions fiables et durables.',
    slogan1: 'Au-dela des Standards, Solutions Transformateurs Sur Mesure',
    slogan2: 'Quel Que Soit Votre Besoin, Astavolt Produit.',
  },
  es: {
    heroTitle: 'Soluciones de Potencia a Medida',
    heroP1: 'En Astavolt ofrecemos soluciones integrales para transformadores de tipo especial disenados y fabricados especificamente para las necesidades de nuestros clientes.',
    heroP2: 'Nuestros transformadores, disenados por nuestro equipo de ingenieria, se utilizan en muchas aplicaciones criticas.',
    heroP3: 'Desarrollamos soluciones adecuadas para cada proyecto con diferentes niveles de tension y opciones de diseno.',
    typesTitle: 'Transformadores de Tipo Especial que Fabricamos',
    types: [
      { title: 'Transformadores Monofasicos', desc: 'Fabricamos transformadores monofasicos para sistemas ferroviarios y proyectos industriales.' },
      { title: 'Transformadores de Aislamiento', desc: 'Nuestros transformadores de aislamiento garantizan continuidad energetica en hospitales y centros de datos.' },
      { title: 'Autotransformadores', desc: 'Nuestros autotransformadores ofrecen alta eficiencia en conversion de tension.' },
      { title: 'Transformadores de Puesta a Tierra', desc: 'Transformadores de puesta a tierra para crear neutro artificial en redes.' },
      { title: 'Transformadores de Salida Lateral', desc: 'Nuestros transformadores de salida lateral facilitan la instalacion en espacios reducidos.' },
      { title: 'Diseno Personalizado', desc: 'Cada proyecto es diferente. Desarrollamos soluciones de transformadores personalizadas.' },
    ],
    features: ['Diseno especifico del proyecto', 'Produccion conforme a IEC', 'Pruebas de fabrica', 'Capacidad de produccion local', 'Soporte de ingenieria', 'Entrega rapida y servicio tecnico'],
    featureFooter: 'Nuestro equipo de expertos esta a su lado para soluciones confiables y duraderas.',
    slogan1: 'Mas Alla de los Estandares, Soluciones a Medida',
    slogan2: 'Sea Cual Sea Su Necesidad, Astavolt Produce.',
  },
  ar: {
    heroTitle: 'حلول طاقة مخصصة لمشروعك',
    heroP1: 'في استرافولت نقدم حلولا شاملة للمحولات المصممة والمصنعة خصيصا لاحتياجات عملائنا.',
    heroP2: 'محولاتنا المصممة لكل مشروع تستخدم في العديد من التطبيقات الحرجة.',
    heroP3: 'نطور حلولا مناسبة لكل مشروع مع مستويات جهد وخيارات تصميم مختلفة.',
    typesTitle: 'المحولات الخاصة التي ننتجها',
    types: [
      { title: 'محولات احادية الطور', desc: 'ننتج محولات احادية الطور لانظمة السكك الحديدية والمشاريع الصناعية.' },
      { title: 'محولات العزل', desc: 'محولات العزل لدينا تضمن استمرارية الطاقة في المستشفيات ومراكز البيانات.' },
      { title: 'المحولات الذاتية', desc: 'محولاتنا الذاتية توفر كفاءة عالية في تحويل الجهد.' },
      { title: 'محولات التاريض', desc: 'محولات تاريض لانشاء نقطة محايدة في الشبكات.' },
      { title: 'محولات الخروج الجانبي', desc: 'محولاتنا ذات الخروج الجانبي توفر سهولة التركيب في المساحات الضيقة.' },
      { title: 'تصميم مخصص لاحتياجاتك', desc: 'كل مشروع مختلف. نطور حلول محولات مخصصة وفق المواصفات الفنية.' },
    ],
    features: ['تصميم خاص بالمشروع', 'انتاج متوافق مع IEC', 'اختبارات المصنع', 'قوة الانتاج المحلي', 'دعم هندسي', 'تسليم سريع ودعم فني'],
    featureFooter: 'فريقنا المتخصص بجانبكم لحلول محولات موثوقة وطويلة العمر.',
    slogan1: 'ما وراء المعايير، حلول محولات مخصصة لمشروعك',
    slogan2: 'مهما كانت حاجتك، استرافولت تنتج.',
  },
  fa: {
    heroTitle: 'راه حل های برق سفارشی برای پروژه شما',
    heroP1: 'در استراولت ما راه حل های جامعی برای ترانسفورمرهای ویژه طراحی و تولید شده بر اساس نیاز مشتریان ارائه می دهیم.',
    heroP2: 'ترانسفورمرهای ما که توسط تیم مهندسی طراحی می شوند در کاربردهای حیاتی بسیاری استفاده می شوند.',
    heroP3: 'ما راه حل های مناسب برای هر پروژه با سطوح ولتاژ و گزینه های طراحی مختلف توسعه می دهیم.',
    typesTitle: 'ترانسفورمرهای ویژه ای که تولید می کنیم',
    types: [
      { title: 'ترانسفورمرهای تک فاز', desc: 'تولید ترانسفورمرهای تک فاز برای سیستم های ریلی و پروژه های صنعتی.' },
      { title: 'ترانسفورمرهای ایزوله', desc: 'ترانسفورمرهای ایزوله ما تداوم انرژی را در بیمارستان ها تضمین می کنند.' },
      { title: 'اتوترانسفورمرها', desc: 'اتوترانسفورمرهای ما بازده بالایی در تبدیل ولتاژ ارائه می دهند.' },
      { title: 'ترانسفورمرهای زمین', desc: 'ترانسفورمرهای زمین برای ایجاد نقطه خنثی مصنوعی در شبکه ها.' },
      { title: 'ترانسفورمرهای خروج جانبی', desc: 'ترانسفورمرهای خروج جانبی ما نصب آسان در فضاهای محدود را فراهم می کنند.' },
      { title: 'طراحی سفارشی', desc: 'هر پروژه متفاوت است. ما راه حل های ترانسفورمر سفارشی توسعه می دهیم.' },
    ],
    features: ['طراحی اختصاصی پروژه', 'تولید مطابق IEC', 'آزمایش های کارخانه', 'قدرت تولید داخلی', 'پشتیبانی مهندسی', 'تحویل سریع و خدمات فنی'],
    featureFooter: 'تیم متخصص ما برای راه حل های ترانسفورمر قابل اعتماد در کنار شماست.',
    slogan1: 'فراتر از استانداردها، راه حل های ترانسفورمر سفارشی',
    slogan2: 'هر نیازی داشته باشید، استراولت تولید می کند.',
  },
};

const typeImages = [CUSTOM_ACTIVE, CUSTOM_ISO, CUSTOM_CORE, CUSTOM_ASSEMBLY, CUSTOM_SIDE, CUSTOM_HERO];
const typeIcons = [<Zap className="w-5 h-5" />, <Shield className="w-5 h-5" />, <RotateCcw className="w-5 h-5" />, <Globe2 className="w-5 h-5" />, <Wrench className="w-5 h-5" />, <Settings className="w-5 h-5" />];

const DIST_SPECS = [
  { label: 'Power Range', value: '50 - 2,500 kVA' },
  { label: 'Max Voltage', value: '36 kV' },
  { label: 'Type', value: 'Three-phase' },
  { label: 'Design', value: 'Conservator / Hermetic' },
];
const POWER_SPECS = [
  { label: 'Power Range', value: '2,500 - 16,000 kVA' },
  { label: 'Max Voltage', value: '36 kV' },
  { label: 'Type', value: 'Three-phase' },
  { label: 'Design', value: 'Conservator / Hermetic' },
];

function ProductTab({ title, desc, img, specs, t }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
      <div className="relative overflow-hidden rounded-sm">
        <img src={img} alt={title} className="w-full h-[350px] md:h-[450px] object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#05060A]/40 to-transparent" />
      </div>
      <div>
        <h3 className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-4">{title}</h3>
        <p className="text-base text-slate-300 leading-relaxed mb-6">{desc}</p>
        {specs && (
          <div className="grid grid-cols-2 gap-4 mb-8">
            {specs.map((spec, i) => (
              <div key={i} className="border border-white/10 bg-[#0B0E14] p-4">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{spec.label}</p>
                <p className="text-sm text-white font-medium">{spec.value}</p>
              </div>
            ))}
          </div>
        )}
        <Link to="/contact">
          <Button data-testid="product-contact-btn" className="bg-blue-600 text-white rounded-sm px-8 py-4 font-bold uppercase tracking-widest hover:bg-blue-500 text-sm">
            {t('common.get_in_touch')} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>
    </div>
  );
}

function CustomTabContent({ language, t }) {
  const cc = customContent[language] || customContent.en;

  return (
    <div data-testid="custom-tab-content">
      {/* Hero intro */}
      <Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center mb-16">
          <div className="relative overflow-hidden rounded-sm">
            <img src={CUSTOM_HERO} alt={cc.heroTitle} className="w-full h-[350px] md:h-[450px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#05060A]/40 to-transparent" />
          </div>
          <div>
            <h3 className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-6">{cc.heroTitle}</h3>
            <p className="text-base text-slate-300 leading-relaxed mb-4">{cc.heroP1}</p>
            <p className="text-sm text-slate-400 leading-relaxed mb-4">{cc.heroP2}</p>
            <p className="text-sm text-slate-400 leading-relaxed">{cc.heroP3}</p>
          </div>
        </div>
      </Reveal>

      {/* Types title */}
      <Reveal>
        <div className="mb-12">
          <p className="text-xs text-blue-400 uppercase tracking-[0.2em] font-medium mb-3">Astravolt</p>
          <h3 className="text-xl sm:text-2xl font-bold tracking-tight text-white">{cc.typesTitle}</h3>
        </div>
      </Reveal>

      {/* Types grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
        {cc.types.map((type, i) => (
          <Reveal key={i}>
            <div className="group border border-white/10 bg-[#05060A] overflow-hidden hover:bg-[#111620] transition-all duration-300 hover:-translate-y-1">
              <div className="relative h-48 overflow-hidden">
                <img src={typeImages[i]} alt={type.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#05060A] via-[#05060A]/20 to-transparent" />
                <div className="absolute top-4 left-4 w-9 h-9 rounded-sm bg-blue-600/90 flex items-center justify-center text-white backdrop-blur-sm">
                  {typeIcons[i]}
                </div>
              </div>
              <div className="p-6">
                <h4 className="text-base font-bold text-white mb-2 tracking-tight">{type.title}</h4>
                <p className="text-sm text-slate-400 leading-relaxed">{type.desc}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      {/* Features */}
      <Reveal>
        <div className="border border-white/10 bg-[#05060A] p-8 md:p-12 mb-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {cc.features.map((f, i) => (
              <div key={i} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-slate-300">{f}</span>
              </div>
            ))}
          </div>
          <p className="text-sm text-slate-400 leading-relaxed italic border-l-2 border-blue-600/40 pl-4">
            {cc.featureFooter}
          </p>
        </div>
      </Reveal>

      {/* Slogan banner */}
      <Reveal>
        <div
          className="relative rounded-sm overflow-hidden py-16 md:py-20 px-8 md:px-16 text-center"
          style={{
            background: 'linear-gradient(135deg, #0B0E14 0%, #111a2e 50%, #0B0E14 100%)',
            boxShadow: '0 0 60px rgba(59, 130, 246, 0.08), inset 0 1px 0 rgba(59, 130, 246, 0.1)',
          }}
        >
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(59,130,246,1) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,1) 1px, transparent 1px)', backgroundSize: '50px 50px' }} />
          <div className="relative">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-4 leading-tight">
              {cc.slogan1}
            </h2>
            <p className="text-lg sm:text-xl font-bold text-blue-400 tracking-wide">
              {cc.slogan2}
            </p>
            <Link to="/contact" className="inline-block mt-8">
              <Button data-testid="custom-contact-cta" className="bg-blue-600 text-white rounded-sm px-10 py-5 font-bold uppercase tracking-widest hover:bg-blue-500 text-sm">
                {t('common.get_in_touch')} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </Reveal>
    </div>
  );
}

export default function Products() {
  const { t, language } = useLanguage();

  return (
    <div data-testid="products-page">
      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-[#05060A] grid-pattern">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs text-blue-400 uppercase tracking-[0.2em] font-medium mb-3">Astravolt</p>
          <h1 data-testid="products-title" className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white mb-4">
            {t('products_section.label')}
          </h1>
          <p className="text-base md:text-lg text-slate-300 max-w-xl">{t('products_section.subtitle')}</p>
        </div>
      </section>

      {/* Products Tabs */}
      <section className="py-20 md:py-32 bg-[#0B0E14]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Tabs defaultValue="distribution" className="w-full">
            <TabsList data-testid="product-tabs" className="w-full max-w-xl bg-[#05060A] border border-white/10 p-1 mb-12 rounded-sm h-auto flex">
              <TabsTrigger value="distribution" data-testid="tab-distribution" className="flex-1 py-3 text-xs sm:text-sm font-bold uppercase tracking-wider rounded-sm data-[state=active]:bg-blue-600 data-[state=active]:text-white text-slate-400">
                {t('products_section.distribution.title')}
              </TabsTrigger>
              <TabsTrigger value="power" data-testid="tab-power" className="flex-1 py-3 text-xs sm:text-sm font-bold uppercase tracking-wider rounded-sm data-[state=active]:bg-blue-600 data-[state=active]:text-white text-slate-400">
                {t('products_section.power.title')}
              </TabsTrigger>
              <TabsTrigger value="custom" data-testid="tab-custom" className="flex-1 py-3 text-xs sm:text-sm font-bold uppercase tracking-wider rounded-sm data-[state=active]:bg-blue-600 data-[state=active]:text-white text-slate-400">
                {t('products_section.custom.title')}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="distribution">
              <Reveal>
                <ProductTab
                  title={t('products_section.distribution.title')}
                  desc={t('products_section.distribution.desc')}
                  img={TRANSFORMER_1}
                  t={t}
                  specs={DIST_SPECS}
                />
              </Reveal>
            </TabsContent>

            <TabsContent value="power">
              <Reveal>
                <ProductTab
                  title={t('products_section.power.title')}
                  desc={t('products_section.power.desc')}
                  img={TRANSFORMER_2}
                  t={t}
                  specs={POWER_SPECS}
                />
              </Reveal>
            </TabsContent>

            <TabsContent value="custom">
              <CustomTabContent language={language} t={t} />
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 md:py-32 bg-[#05060A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-4">{t('contact_section.subtitle')}</h2>
            <Link to="/contact">
              <Button data-testid="products-contact-cta" className="bg-blue-600 text-white rounded-sm px-10 py-6 font-bold uppercase tracking-widest hover:bg-blue-500 text-sm">
                {t('common.get_in_touch')} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
